<template>
  <div ref="chartRef"></div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";

import { keyBy } from 'lodash';
import { RESPORTS_TYPE_MAP } from '~/contants/reports';
import * as echarts from "echarts/core";
import { LineChart, BarChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from "echarts/components";

import moment from "moment";

const props = defineProps({
  dataList: { default: [] },
  width: { default: 840 },
  height: { default: 400 },
  isPercent: { default: false },
  showDate: { default: true },
  showYName: { default: 'commission' },
  showXName: { default: 'startDate' },
  lineColor: { default: '#835CF2' },
  lineBgOffsetZero: { default: 'rgb(131, 92, 242, 0)' },
  lineBgOffsetTop: { default: 'rgb(131, 92, 242, .3)' },
  pageType: { default: '' },
  chartType: { default: 'line' },
  showCurrent: { default: true },
  startTimestamp: { default: moment().subtract(15, 'day').valueOf() },
  onClickBarChart: { default: null },
  endTimestamp: { default: moment().valueOf() },
  metricName: { default: '' },
  selectDaysNum: { default: 'days15' } ,//days15, days15to30, days31to90, days91to365
});

const chartRef = ref();
let commissionHistoryChart = null;

onMounted(() => {
  echarts.use([
    BarChart,
    LineChart,
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LegendComponent,
    CanvasRenderer,
  ]);
});

const setChart = () => {
  let xAxis, yAxis, chartOptions, showYName = props.showYName, showXName = props.showXName, dataListMap = {};
  // 柱状图需要交换xy轴的数据
  if (props.chartType === 'bar') {
    const tempName = showXName;
    showXName = showYName;
    showYName = tempName;
  }

  let scale = 0, xData = [], yData = [];
  const intervalDays = moment(props.endTimestamp).diff(moment(props.startTimestamp), 'days');
  // 15天以内刻度1、16-30天，刻度2,31-90天，刻度5，91-365,刻度15
  if (intervalDays <= 15) {
    scale = 0
  } else if (intervalDays <= 31) {
    scale = 1
  } else if (intervalDays <= 90) {
    scale = 4;
  } else if (intervalDays <= 365) {
    scale = 14;
  }
  if (props.showDate) {
    xData = [moment(props.startTimestamp).format("MMM DD")];
    let tempTime = props.startTimestamp;
    for (let i = 0; i < intervalDays; i++) {
      tempTime = moment(tempTime).add(1, 'days').valueOf();
      if (tempTime > props.endTimestamp) {
        tempTime = props.endTimestamp;
      }
      const itemDate = moment(tempTime).format("MMM DD");
      xData = [...xData, itemDate];
    }
    if (props.dataList.length) {
      const originalData = props.dataList.map(item => {
        item.timeDate = moment(item.reportDateTimestamp).format("MMM DD");
        return item;
      })
      xData.forEach(item => {
        const hasData = originalData.find(j => j.timeDate === item);
        if (hasData) {
          yData = [...yData, hasData[showYName]];
        } else {
          yData = [...yData, null]
        }
      })
    }

  } else {
    if (props.dataList.length) {
      dataListMap = keyBy(props.dataList, showXName);
      xData = props.dataList.map(item => {
        let itemData = '';
        if(props.pageType === 'trackingId'){
          if(props.showYName === 'primaryTrackingId'){
            itemData = item[showXName]
          }else{
            itemData =`${item.primaryTrackingId}-${item[showXName]}` 
          }
        }else{
          if (moment(item[showXName]).isValid()) {
          itemData = moment(item[showXName]).format("MMM DD")
          } else {
          itemData = item[showXName];
          }
        }
        return itemData;
      }).reverse();
      yData = props.dataList.map((item) => item[showYName]).reverse();
      yData.push(props.dataList[0][showYName]);
     }
  }

  xAxis = {
    type: "category",
    data: xData,
    axisLine: {
      lineStyle: {
        color: "rgba(15, 6, 41, .5)",
      },
    },
    axisLabel: {
      fontSize: 10,
      interval: props.chartType === 'bar' ? 0 : scale,
    },
  };

  yAxis = {
    type: "value",
    axisLabel: {
      formatter: props.isPercent ? "{value}%" : '{value}',
    },
    axisLine: {
      lineStyle: {
        color: "rgba(15, 6, 41, .7)",
      },
    },
  };

  // 柱状图需要交换xy轴的数据
  if (props.chartType === 'bar') {
    const tempAxis = xAxis;
    xAxis = yAxis;
    yAxis = props.showYName == 'productTitle' ? {
      ...tempAxis,
      axisLabel: {
        width: 260,
        ...tempAxis.axisLabel,
        overflow: 'truncate', // 超出部分截断
        ellipsis: '...' // 显示省略号
      }
    } : tempAxis;
  }

  chartOptions = {
    grid: {
      left: props.showYName == 'productTitle' ? "4%" : "1%",
      right: props.chartType === 'bar' ? "4%" : "1%",
      bottom: "5%",
      top: "5%",
      containLabel: true,
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#fff",
      borderColor: "#fff",
      padding: 0,
      extraCssText: "box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);",
      formatter: (params) => {
        const { name, value } = params[0];
        let itemData = dataListMap[name], itemImg = '';
        if (itemData&&props.pageType !== 'trackingId') {
          itemImg = itemData.brandLogoImageUrl || itemData.productImageUrl || itemData.publisherHeadImageUrl || '';
          itemImg = itemImg ? `<img src="${itemImg}" />` : `<span class="no-img">${itemData.brandName ? `<em class="brand-name">${itemData.brandName}</em>` : ''}</span>`
        }
        const toolTipName = RESPORTS_TYPE_MAP[props.metricName].name;
        const res = `<div class="chart-tag ${itemData ? 'has-img' : ''}">
            <div class="date-text">${name}</div>
            <div class="top-cont">
              <span class="tag-name">
                <span class="circle ${props.metricName}"></span>
                ${toolTipName}
              </span>
              <span class="value">${value ? (props.isPercent ? value + '%' : value) : '--'}</span>
            </div>
            <div class="bottom-cont">
              ${itemImg}
              <span class="text">${props.pageType == 'sellerProduct' ? itemData['productAsin'] : name}</span>
            </div>
          </div>`
        return res;
      }
    },
    xAxis: xAxis,
    yAxis: yAxis,
    series: [
      {
        connectNulls: true,
        data: yData,
        type: props.chartType,
        color: props.lineColor,
        showSymbol: true, // 显示标记点
        symbolSize: 8, // 标记点大小
        barWidth: '16px',
        symbol: 'circle', // 显示为圆形标记
        showSymbol: true, // 显示标记点
        showAllSymbol: true, // 强制显示所有点的标记
        label: {
          show: props.chartType === 'bar',
          position: 'right',
          formatter: props.isPercent ? "{c}%" : '{c}',
        },
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          borderRadius: [0, 4, 4, 0]
        },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: props.lineBgOffsetTop,// 100% 处的颜色
              },
              {
                offset: 1,
                color: props.lineBgOffsetZero,  // 0% 处的颜色 
              },
            ],
            global: false, // 缺省为 false
          },
        },
      },
    ],
  };

  commissionHistoryChart.setOption(chartOptions);
  if (props.onClickBarChart) {
    commissionHistoryChart.off().on("click", params => {
      console.log(params,'params')
      let name = params.name
      if(name.indexOf("-") !== -1&&props.pageType === 'trackingId'){
        name = name.split("-")
      }
      
      // 使用dataIndex来获取准确的数据项，避免name重复问题
      let completeItemData = null;
      if (params.dataIndex !== undefined && props.dataList && props.dataList.length > 0) {
        if (props.showDate) {
          // 对于按日期显示的图表，通过日期匹配找到对应数据项
          const targetDate = moment(props.startTimestamp).add(params.dataIndex, 'days').format("MMM DD");
          completeItemData = props.dataList.find(item => {
            return moment(item.reportDateTimestamp).format("MMM DD") === targetDate;
          });
        } else {
          // 对于非日期数据，由于数据被reverse了，需要计算实际索引
          const actualIndex = props.dataList.length - 1 - params.dataIndex;
          completeItemData = props.dataList[actualIndex];
          // 如果计算的索引无效，尝试直接使用dataIndex
          if (!completeItemData && params.dataIndex < props.dataList.length) {
            completeItemData = props.dataList[params.dataIndex];
          }
        }
      }
      
      // 如果通过dataIndex没找到，再尝试用name从dataListMap获取（兜底方案）
      if (!completeItemData) {
        completeItemData = dataListMap[params.name] || null;
      }
      
      // 传递包含name和完整数据项的对象
      props.onClickBarChart({
        name: name,
        itemData: completeItemData,
        originalParams: params
      });
    });
  }
};

watch([props, chartRef], async () => {
  if (chartRef) {
    if (!commissionHistoryChart) {
      commissionHistoryChart = echarts.init(chartRef.value, null, {
        width: props.width < 1123 ? 1123 : props.width,
        height: props.height
      });
    }
    setChart();
  }
});

watch(() => props.width, async () => {
  if (chartRef && commissionHistoryChart) {
    commissionHistoryChart.resize({
      width: props.width < 1123 ? 1123 : props.width,
      height: props.height
    });
  }
});
</script>

<style lang="less">
@import '~/assets/less/ellipsis.less';

.chart-tag {
  min-width: 143px;
  padding-top: 12px;

  &.has-img {
    min-width: 244px;

    .bottom-cont {
      display: flex;
    }

    .date-text {
      display: none;
    }
  }

  .date-text {
    padding: 0 12px 0 12px;
    color: var(--dark-4);
    font-size: 12px;
    letter-spacing: 0.3px;
    line-height: 16px;
  }

  .bottom-cont {
    display: none;
    padding: 12px 12px 8px 8px;
    border-top: 1px solid var(--border-4);

    .text {
      font-weight: 500;
      font-size: 14px;
      color: var(--dark-2);
      letter-spacing: 0.3px;
      line-height: 22px;
      margin-left: 8px;
      max-width: 240px;
      align-self: center;
      .multiple-ellipsis(2);
      white-space: break-spaces;
    }

    .no-img {
      width: 56px;
      height: 56px;
      position: relative;
      border-radius: 8px;
      background-color: var(--bg-gray-2);

      >.brand-name {
        top: 50%;
        left: 50%;
        width: 56px;
        margin: 0 auto;
        padding: 0 4px;
        font-size: 11px;
        overflow: hidden;
        max-height: 39px;
        line-height: 13px;
        position: absolute;
        text-align: center;
        font-style: normal;
        white-space: normal;
        color: var(--dark-4);
        word-break: break-all;
        letter-spacing: 0.3px;
        transform: translate(-50%, -50%);
      }
    }

    img {
      width: 56px;
      height: 56px;
      border-radius: 8px;
      object-fit: contain;
    }
  }

  .top-cont {
    padding: 0 12px 12px 12px;
    display: flex;
    color: var(--dark-1);
    font-size: 13px;
    margin-top: 6px;
    justify-content: space-between;

    .tag-name {
      display: flex;
      font-weight: 400;
      align-self: center;

      .circle {
        width: 10px;
        height: 10px;
        border-radius: 5px;
        margin-right: 4px;
        align-self: center;

        &.clicks {
          background: var(--tag-bg-1);
        }

        &.detailPageViews {
          background: var(--tag-bg-2);
        }

        &.addToCart {
          background: var(--tag-bg-3);
        }

        &.conversions {
          background: var(--tag-bg-4);
        }

        &.conversionsRate {
          background: var(--tag-bg-9);
        }

        &.salesAmount {
          background: var(--tag-bg-5);
        }

        &.acosAmount {
          background: var(--tag-bg-1);
        }

        &.discountedSalesAmount {
          background: var(--tag-bg-8);
        }

        &.commissionAmount,
        &.commissionAndFeeAmount {
          background: var(--tag-bg-6);
        }

        &.bonusAmount {
          background: var(--tag-bg-7);
        }
      }
    }

    .value {
      align-self: center;
      margin-left: 27px;
      font-weight: 500;
    }
  }
}
</style>